import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Paintbrush,
  ShoppingCart,
  CreditCard,
  Download,
  Lightbulb,
  ArrowRight,
  CheckCircle2,
  Star,
  Zap
} from "lucide-react";
import './ContentStyles.css';
import './HowItWorks.css';

function HowItWorksContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      icon: Sparkles,
      titleKey: "howItWorks.step1Title",
      descKey: "howItWorks.step1Desc",
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)"
    },
    {
      number: 2,
      icon: Paintbrush,
      titleKey: "howItWorks.step2Title",
      descKey: "howItWorks.step2Desc",
      color: "#3b82f6",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)"
    },
    {
      number: 3,
      icon: ShoppingCart,
      titleKey: "howItWorks.step3Title",
      descKey: "howItWorks.step3Desc",
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)"
    },
    {
      number: 4,
      icon: CreditCard,
      titleKey: "howItWorks.step4Title",
      descKey: "howItWorks.step4Desc",
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)"
    },
    {
      number: 5,
      icon: Download,
      titleKey: "howItWorks.step5Title",
      descKey: "howItWorks.step5Desc",
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)"
    }
  ];

  const tips = [
    { key: "howItWorks.tip1", icon: Lightbulb },
    { key: "howItWorks.tip2", icon: Star },
    { key: "howItWorks.tip3", icon: Zap }
  ];

  return (
    <div className="content-wrapper">
      <div className="content-main hiw-page">
        {/* Hero Section */}
        <div className="hiw-hero">
          <div className="hiw-hero-glow" />
          <div className="hiw-hero-content">
            <div className="hiw-hero-badge">
              <Sparkles size={14} />
              <span>{t("howItWorks.heroBadge")}</span>
            </div>
            <h1 className="hiw-hero-title">{t("howItWorks.heroTitle")}</h1>
            <p className="hiw-hero-subtitle">{t("howItWorks.heroSubtitle")}</p>
          </div>
        </div>

        {/* Steps Section */}
        <div className="hiw-steps-container">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.number} className="hiw-step-wrapper">
                <div className="hiw-step-card">
                  <div className="hiw-step-icon-area" style={{ background: step.gradient }}>
                    <Icon size={28} color="white" strokeWidth={2} />
                    <div className="hiw-step-number">{step.number}</div>
                  </div>
                  <div className="hiw-step-content">
                    <h3 className="hiw-step-title">{t(step.titleKey)}</h3>
                    <p className="hiw-step-desc">{t(step.descKey)}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="hiw-step-connector">
                    <div className="hiw-connector-line" />
                    <ArrowRight size={16} className="hiw-connector-arrow" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tips Section */}
        <div className="hiw-tips-section">
          <h2 className="hiw-tips-title">
            <Lightbulb size={22} />
            <span>{t("howItWorks.tipsTitle")}</span>
          </h2>
          <div className="hiw-tips-grid">
            {tips.map((tip, index) => {
              const TipIcon = tip.icon;
              return (
                <div key={index} className="hiw-tip-card">
                  <div className="hiw-tip-icon">
                    <TipIcon size={20} />
                  </div>
                  <p className="hiw-tip-text">{t(tip.key)}</p>
                  <CheckCircle2 size={16} className="hiw-tip-check" />
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="hiw-cta-section">
          <div className="hiw-cta-glow" />
          <h2 className="hiw-cta-title">{t("howItWorks.ctaTitle")}</h2>
          <p className="hiw-cta-desc">{t("howItWorks.ctaDesc")}</p>
          <button
            className="hiw-cta-button"
            onClick={() => navigate("/dashboard/new-design")}
          >
            <Sparkles size={18} />
            <span>{t("howItWorks.ctaButton")}</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default HowItWorksContent;
